import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Grid, LinearProgress, linearProgressClasses, Stack, styled } from '@mui/material';
import Typography from '@mui/material/Typography';
import MainCard from 'components/MainCard';
import ImageContainer from 'modules/networksapps/ImageContainer';
import KeyValueList from 'components/shared/list/KeyValueList';
import CoverageMap from 'modules/networksapps/CoverageMap';
import TokenService from 'services/token/Token';
import { useTranslation } from 'react-i18next';
import PlanInstall from 'modules/modals/PlanDetails/components/PlanInstallAction';
import TruncateList from 'components/shared/list/TruncateList';
import { defaultTo, isNil } from 'lodash';
import { getFormattedPrice } from 'utils/functions';

const BorderLinearProgress = styled(LinearProgress)(({ theme, thresholdReached, value }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: value === 100 || thresholdReached ? theme.palette.error.main : theme.palette.custom.main,
  },
}));

const styles = {
  modalStyles: {
    width: '100%',

    background: '#F4F5F5',
    borderRadius: 8,
    outline: 'none',
    overflow: 'auto',
  },
  typographyStyles: {
    width: '211px',
    height: '29px',
    fontSize: '24px',
    fontWeight: 700,
    lineHeight: '29px',
    letterSpacing: '0px',
    textAlign: 'left',
    /* Add any additional styles here */
  },
  detailsHeaderStyles: {
    width: '62px',
    height: '22px',
    fontSize: '18px',
    fontWeight: 700,
    lineHeight: '22px',
    letterSpacing: '0px',
    textAlign: 'left',
    color: '#111',
    whiteSpace: 'nowrap',
    /* Add any additional styles here */
  },
  detailsValueStyles: {
    fontFamily: 'Inter',
    fontSize: '18px',
    fontWeight: 'Normal',
    lineHeight: '22px',
    letterSpacing: '0px',
    textAlign: 'left',
    /* Add any additional styles here */
  },

  closeButton: {
    // ... (other button styles)
    textDecoration: 'none', // Add this line to remove underline
    '&:focus': {
      outline: 'none',
    },
    '&:focus-visible': {
      boxShadow: '0 0 0 2px #aaa',
    },
  },
  pricePerMbStyles: {
    width: '135px',
    height: '31px',
    fontSize: '26px',
    fontWeight: 700,
    lineHeight: '31px',
    letterSpacing: '0px',
    textAlign: 'left',
    color: '#008986', // Dark TEAL

    /* Add any additional styles here */
  },
  accessPriceStyles: {
    width: '148px',
    height: '19px',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '19px',
    letterSpacing: '0px',
    textAlign: 'left',
    /* Add any additional styles here */
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  checkbox: {
    marginRight: '0', // Remove right margin
    padding: '0', // Remove padding
  },
};

const calculateInventoryProgress = ({ usedProfiles, byocPoolSize, byocLowInventoryThreshold }) => {
  if (usedProfiles === 0 || byocPoolSize === 0) return 0;
  return Math.min((usedProfiles / byocPoolSize) * 100, 100);
};

const Inventory = ({ row }) => {
  const { byocAvailable = 0, byocPoolSize = 0, byocLowInventoryThreshold = 0 } = row;
  const usedProfiles = Math.max(byocPoolSize - byocAvailable, 0);
  const profilesPercentage = calculateInventoryProgress({
    usedProfiles,
    byocPoolSize,
    byocLowInventoryThreshold,
  });
  return (
    <KeyValueList.Item name={'Inventory levels used'}>
      <Stack direction={'row'} spacing={2} sx={{ alignItems: 'center !important' }} alignItems={'center'}>
        <Box width={'150px'}>
          <BorderLinearProgress
            variant="determinate"
            thresholdReached={byocAvailable <= byocLowInventoryThreshold}
            value={profilesPercentage}
          />
        </Box>
        <Typography>
          {usedProfiles}/{byocPoolSize}
        </Typography>
      </Stack>
    </KeyValueList.Item>
  );
};

const SkuList = ({ data, limit = 4 }) => {
  const totalData = data.length;
  const renderVisible = (data, index, arr) => {
    const total = arr.length;
    let value = data;
    const lastIndex = Math.min(total, limit);
    if (index !== lastIndex - 1) {
      value += ', ';
      return value;
    }
    const hasMore = totalData - total > 0;
    if (index === lastIndex - 1 && total > 1 && hasMore) {
      value += '... ';
      return value;
    }

    return value;
  };

  const renderHidden = (data, index, arr) => {
    const total = arr.length - 1;
    let value = data;
    if (total >= 1 && index < total) {
      value += ', ';
    }
    return value;
  };
  return (
    <TruncateList
      limit={limit}
      data={data}
      renderVisible={renderVisible}
      renderHidden={renderHidden}
      transform={(data) => data.sku}
      defaultValue={'-'}
      sx={{
        '& .TruncateListHiddenCount': {
          color: '#00579F',
        },
      }}
    />
  );
};

const PlanDescriptionInfo = ({ item }) => {
  const { t } = useTranslation();
  return (
    <KeyValueList>
      <KeyValueList.Item name={t('views.plan_details.plan_description_info.rat')}>
        {item?.networkTechTypes?.join('/') ?? '-'}
      </KeyValueList.Item>
      <KeyValueList.Item name={t('views.plan_details.plan_description_info.credential_type')}>
        {defaultTo(item?.credentialsType, '-')}
      </KeyValueList.Item>
      <KeyValueList.Item name={t('views.plan_details.plan_description_info.coverage_type')}>
        {defaultTo(item?.coverageType, '-')}
      </KeyValueList.Item>
      <KeyValueList.Item name={t('views.plan_details.plan_description_info.mcc_mnc')}>-</KeyValueList.Item>
      <KeyValueList.Item name={t('views.plan_details.plan_description_info.network_type')}>
        {defaultTo(item?.networkType, '-')}
      </KeyValueList.Item>
      <KeyValueList.Item name={t('views.plan_details.plan_description_info.smsc')}>-</KeyValueList.Item>
      <KeyValueList.Item name={t('views.plan_details.plan_description_info.sku')}>
        <SkuList data={item.storeUnits} />
      </KeyValueList.Item>
      <KeyValueList.Item name={t('views.plan_details.plan_description_info.network_app_uuid')}>
        {item?.planUuid}
      </KeyValueList.Item>

      {item?.byoc && <Inventory row={item} />}
    </KeyValueList>
  );
};

const PlanPriceInfo = ({ item }) => {
  const { t, i18n } = useTranslation();
  const tokenService = TokenService.getData();
  return (
    <KeyValueList>
      <KeyValueList.Item name={t('views.plan_details.plan_price_info.overage')}>
        {getFormattedPrice(item, (item, { overage }) => {
          return item.byoc && item?.pice === 0 ? '-' : `${overage}`;
        })}
      </KeyValueList.Item>
      <KeyValueList.Item name={t('views.plan_details.plan_price_info.sms_mt')}>
        {getFormattedPrice(item, (item, { formatter }) => {
          return item.byoc && item?.smsPrice === 0 ? '-' : `${formatter.format(item?.smsPrice, 'currency')}`;
        })}
      </KeyValueList.Item>
      <KeyValueList.Item name={t('views.plan_details.plan_price_info.sms_mo')}>-</KeyValueList.Item>
    </KeyValueList>
  );
};

const CoverageCountriesList = ({ item }) => {
  const { t, i18n } = useTranslation();
  const coverageCountriesNamesList = useMemo(() => {
    const countries =
      item?.coverageCountries?.map((item) => {
        return item.name;
      }) || [];
    return countries.join(', ');
  }, [item]);

  const [_, setShowCoverageMap] = useState(true);

  const toggleShowMap = () => {
    setShowCoverageMap((show) => !show);
  };

  return (
    <>
      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography style={styles.detailsHeaderStyles} variant="h6">
          {t('views.plan_details.coverage')}
        </Typography>
        <Button onClick={toggleShowMap}>{t('views.plan_details.see_map')}</Button>
      </Box>

      <Box style={{ maxHeight: '100px', overflowY: 'auto', width: '270px' }}>
        <Typography style={{ ...styles.detailsValueStyles }} variant="h6">
          {coverageCountriesNamesList}
        </Typography>
      </Box>
    </>
  );
};

const PlanDetails = ({ components = {}, componentProps = {}, item }) => {
  const { actions: Actions = () => PlanInstall } = components;
  const { actions: actionsProps = {} } = componentProps;

  const { t, i18n } = useTranslation();

  const [_, setShowCoverageMap] = useState(true);

  return (
    <Box style={styles.modalStyles}>
      <Grid container columnSpacing={{ xs: 1, sm: 1, md: 2 }}>
        {/*Details section*/}
        <Grid item>
          <Grid>
            <MainCard
              sx={{
                width: '304px',
                border: 'none',
                padding: '10px',
              }}
            >
              <Stack spacing={2}>
                <Typography
                  variant="body1"
                  color="#111111"
                  sx={{
                    height: '15px',
                    overflow: 'hidden', // Hide overflow
                    whiteSpace: 'nowrap', // Prevent text from wrapping
                    textOverflow: 'ellipsis', // Add ellipsis for overflow
                  }}
                  style={styles.typographyStyles}
                >
                  {item?.name}
                </Typography>

                <Box>
                  <Typography variant="p" style={styles.pricePerMbStyles}>
                    {getFormattedPrice(item, (item, { price, formatter }) => {
                      return item.byoc && item.price === 0 ? '-' : `${price}`;
                    })}
                  </Typography>
                </Box>

                <Stack spacing={3}>
                  <Typography variant="p" color="#7A7B84" sx={styles.accessPriceStyles}>
                    {t('views.plan_details.access')}:{' '}
                    {getFormattedPrice(item, (item, { formatter }) => {
                      const accessFee = item?.networkAccessPrice ?? item?.networkAccessFee;
                      return item.byoc && accessFee === 0 ? '-' : `${formatter.format(accessFee, 'currency')} / mo`;
                    })}
                  </Typography>

                  {/*images Container*/}
                  <ImageContainer images={item?.planLogoUrls} />

                  <Stack spacing={2}>
                    <PlanPriceInfo item={item} />
                  </Stack>
                  <Stack spacing={3}>
                    <Actions {...actionsProps} />
                  </Stack>
                </Stack>
                {item?.newPlan ? (
                  <Box
                    style={{
                      position: 'absolute',
                      left: '57%',
                      top: '-10px',
                      backgroundColor: '#5264C9', // Background color
                      width: '200px',
                      height: '28px',
                      transform: 'rotate(35deg)', // Rotate the label 35 degrees clockwise
                      display: 'flex',
                      justifyContent: 'center',
                      aligndatas: 'center',
                      color: 'white', // Text color
                      fontFamily: 'Inter',
                      weight: 600,
                    }}
                  >
                    New
                  </Box>
                ) : (
                  ''
                )}
              </Stack>
            </MainCard>
          </Grid>

          <Grid item style={{ marginTop: '25px' }}>
            <MainCard
              sx={{
                backgroundColor: '#fff',
                width: '100%',
                borderRadius: '8px',
                border: 'none',
              }}
            >
              <CoverageCountriesList item={item} />
            </MainCard>
          </Grid>
        </Grid>
        <Grid item flex={1}>
          {/*Description section*/}
          <Grid item>
            <MainCard
              sx={{
                backgroundColor: '#fff',
                width: '100%',
                px: 2,
              }}
            >
              <Stack sx={{ maxHeight: '60px', overflowY: 'auto' }}>
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography style={styles.detailsHeaderStyles} variant="h6">
                    {t('views.plan_details.description')}
                  </Typography>
                </Box>
                <Typography
                  marginTop={'10px'}
                  sx={{
                    backgroundColor: '#fff',
                  }}
                  dangerouslySetInnerHTML={{ __html: item?.description }}
                />
              </Stack>
            </MainCard>
          </Grid>
          {/*KeyValueList section*/}
          <Grid item style={{ marginTop: '25px' }}>
            <MainCard
              sx={{
                backgroundColor: '#fff',
                width: '100%',
                p: 2,
              }}
            >
              <Stack spacing={2}>
                <PlanDescriptionInfo item={item} />
              </Stack>
            </MainCard>
          </Grid>
          <Grid item style={{ marginTop: '25px' }}>
            <MainCard
              sx={{
                backgroundColor: '#fff',
                width: '100%',
                height: '291px',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <Typography style={styles.detailsHeaderStyles} variant="h6">
                {t('views.plan_details.coverage_map')}
              </Typography>
              <Box>
                <CoverageMap item={item?.coverageCountries} />
              </Box>
            </MainCard>
          </Grid>
          {/* Label on the right side, moved further to the right */}
        </Grid>
      </Grid>
    </Box>
  );
};

export default PlanDetails;
